import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FormDataConsumer, useDataProvider, useNotify } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { getChainId } from '../../../lib';
import { GiftProduct, GiftProductType, IntegrationType } from '../../../models';
import { ProductConfigCard } from '../components/products';
import { everyProductSkipsPlaces, findByProductIndex, productNeedsRedemptionPlaces } from './redemptionPlaces';

interface PlaceConfig {
  placeId: string;
  placeName: string;
  integration?: string | null;
  partnerValue: number | null;
  hasProductPlace: boolean;
  rtiCode?: string | null;
  /** rtiCode herdado do product_place (catálogo) — apenas exibição, não é persistido no gift */
  productRtiCode?: string | null;
  isEvoActive?: boolean;
  trevoServiceId?: number | null;
  discountPercentage?: number | null;
}

interface ProductWithPlaces {
  productIndex: number;
  product: GiftProduct;
  places: PlaceConfig[];
}

interface FormPlacesConfigurationInternalProps {
  formData: any;
  fuels?: any[];
  onLoadingChange?: (loading: boolean) => void;
}

const FormPlacesConfiguration: React.FC<FormPlacesConfigurationInternalProps> = ({ formData, fuels = [], onLoadingChange }) => {
  const chainId = getChainId();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { setValue } = useFormContext();

  const [productsWithPlaces, setProductsWithPlaces] = useState<ProductWithPlaces[]>([]);
  const [loading, setLoading] = useState(false);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [allPlacesMap, setAllPlacesMap] = useState<Map<string, any>>(new Map());
  const [allAvailablePlaces, setAllAvailablePlaces] = useState<any[]>([]);
  const [lastProductsHash, setLastProductsHash] = useState<string>('');

  // Notificar mudanças no loading para o componente pai
  useEffect(() => {
    const isLoading = loading || placesLoading;
    onLoadingChange?.(isLoading);
  }, [loading, placesLoading, onLoadingChange]);

  // Carregar todos os locais disponíveis uma vez
  useEffect(() => {
    const loadAllPlaces = async () => {
      try {
        setPlacesLoading(true);
        const { data } = await dataProvider.getList('places', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        });
        setAllAvailablePlaces(data);
      } catch (error) {
        console.error('Erro ao carregar locais:', error);
      }
      finally {
        setPlacesLoading(false);
      }
    };

    loadAllPlaces();
  }, [dataProvider]);

  // Carregar configurações dos produtos apenas quando a lista de produtos mudar
  useEffect(() => {
    if (!formData?.products?.length) {
      setProductsWithPlaces([]);
      setLastProductsHash('');
      return;
    }

    // Criar um hash simples dos produtos para detectar mudanças reais
    const productsHash = JSON.stringify(formData.products.map((p: GiftProduct) => ({
      type: p.type,
      productId: p.productId,
      productName: p.productName,
    })));

    // Só recarrega se a lista de produtos realmente mudou
    if (productsHash !== lastProductsHash) {
      setLastProductsHash(productsHash);
      loadProductsConfiguration();
    }
  }, [formData?.products]);

  const loadProductsConfiguration = async () => {
    console.log('[FormPlacesConfiguration] Loading products configuration...');
    console.log('[FormPlacesConfiguration] giftProductPlaces:', formData.giftProductPlaces);
    console.log('[FormPlacesConfiguration] products:', formData.products);

    setLoading(true);
    try {
      const giftProductPlaces = formData.giftProductPlaces || [];

      // Coletar todos os placeIds únicos
      const allPlaceIds = new Set<string>();
      giftProductPlaces.forEach((gpp: any) => {
        gpp.places?.forEach((p: any) => {
          if (p.placeId) allPlaceIds.add(p.placeId);
        });
      });

      // Buscar informações dos locais se houver algum
      let localPlacesMap = new Map(allPlacesMap);

      if (allPlaceIds.size > 0) {
        const placeIdsArray = Array.from(allPlaceIds);
        const { data: placesData } = await dataProvider.getList('places', {
          filter: { ids: placeIdsArray },
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'id', order: 'ASC' },
        });

        localPlacesMap = new Map(placesData.map((p: any) => [p.id, p]));
        setAllPlacesMap(localPlacesMap);
      }

      // Buscar ProductPlaces para todos os produtos existentes
      const existingProducts = (formData.products || []).filter(
        (p: GiftProduct) => p.type === GiftProductType.Existing && p.productId
      );

      const productPlacesMap = new Map<string, any[]>(); // productId -> productPlaces[]

      await Promise.all(
        existingProducts.map(async (product: GiftProduct) => {
          try {
            const { data: catalogProduct } = await dataProvider.getOne(
              `chains/${chainId}/products`,
              { id: product.productId }
            );
            const productPlaces = catalogProduct?.places || catalogProduct?.productPlaces || [];
            productPlacesMap.set(product.productId!, productPlaces);
          } catch (error) {
            console.error(`Erro ao buscar ProductPlace para produto ${product.productId}:`, error);
            productPlacesMap.set(product.productId!, []);
          }
        })
      );

      // Construir ProductWithPlaces diretamente de giftProductPlaces
      const productsConfigs: ProductWithPlaces[] = (formData.products || [])
        .filter(productNeedsRedemptionPlaces)
        .map((product: GiftProduct, _idx: number) => {
          // Encontrar o índice original do produto no array
          const prodIndex = (formData.products || []).findIndex((p: GiftProduct) => p === product);
          // Buscar a configuração deste produto em giftProductPlaces
          const giftProductPlace = giftProductPlaces.find(
            (gpp: any) => gpp.productIndex === prodIndex
          );

          const placesForProduct: PlaceConfig[] = (giftProductPlace?.places || []).map((placeData: any) => {
            const place = localPlacesMap.get(placeData.placeId) || {
              id: placeData.placeId,
              name: 'Local desconhecido'
            };

            let partnerValue = placeData.partnerValue;
            let hasProductPlace = false;
            let productRtiCode: string | null = null;

            // Se é produto existente, verificar se tem ProductPlace (valor automático)
            if (product.type === GiftProductType.Existing && product.productId) {
              const productPlaces = productPlacesMap.get(product.productId) || [];
              const productPlace = productPlaces.find((pp: any) => pp.placeId === placeData.placeId);

              if (productPlace?.partnerPrice != null) {
                partnerValue = productPlace.partnerPrice;
                hasProductPlace = true;
              }
              // rtiCode automático do catálogo — independe do partnerValue (funciona mesmo com 0/null)
              if (productPlace?.rtiCode != null) {
                productRtiCode = String(productPlace.rtiCode);
              }
            }

            return {
              placeId: place.id,
              placeName: place.name,
              integration: place.integration,
              partnerValue,
              hasProductPlace,
              rtiCode: placeData.rtiCode || null,
              productRtiCode,
              isEvoActive: placeData.isEvoActive || false,
              discountPercentage: placeData.discountPercentage || null,
            };
          });

          return {
            productIndex: prodIndex,
            product,
            places: placesForProduct,
          };
        }
      );

      console.log('[FormPlacesConfiguration] Products configs:', productsConfigs);
      setProductsWithPlaces(productsConfigs);
      console.log('[FormPlacesConfiguration] Configuration loaded successfully');
    } catch (error) {
      console.error('[FormPlacesConfiguration] Erro ao carregar configurações:', error);
      notify('Erro ao carregar configurações dos produtos', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateGiftProductPlaces = (productsConfigs: ProductWithPlaces[]) => {
    // Estrutura product-centric: cada produto tem seus lugares
    const giftProductPlaces = productsConfigs.map((productConfig) => {
      const giftProduct = formData.products?.[productConfig.productIndex];

      return {
        productIndex: productConfig.productIndex,
        productId: giftProduct?.productId || null,
        places: productConfig.places.map((placeConfig) => ({
          placeId: placeConfig.placeId,
          partnerValue: placeConfig.partnerValue,
          hasProductPlace: placeConfig.hasProductPlace,
          rtiCode: placeConfig.rtiCode,
          isEvoActive: placeConfig.isEvoActive,
          trevoServiceId: placeConfig.trevoServiceId,
          discountPercentage: placeConfig.discountPercentage,
        })),
      };
    });

    // Atualizar o form context com setValue
    setValue('giftProductPlaces', giftProductPlaces, { shouldDirty: true, shouldValidate: false });

    // Atualizar placeIds com todos os locais únicos
    const uniquePlaceIds = Array.from(
      new Set(productsConfigs.flatMap(pc => pc.places.map(p => p.placeId)))
    );
    const currentPlaceIds = formData.placeIds || [];

    // Comparar arrays para ver se mudou
    const placeIdsChanged = uniquePlaceIds.length !== currentPlaceIds.length ||
      uniquePlaceIds.some(id => !currentPlaceIds.includes(id));

    if (placeIdsChanged) {
      setValue('placeIds', uniquePlaceIds, { shouldDirty: true, shouldValidate: false });
    }
  };

  const handleAddPlace = async (productIndex: number, placeId: string) => {
    console.log("🚀 ~ handleAddPlace ~ productIndex:", productIndex, "placeId:", placeId);

    // Buscar informações do local
    let place = allPlacesMap.get(placeId);

    if (!place) {
      try {
        const { data: fetchedPlace } = await dataProvider.getOne('places', { id: placeId });
        place = fetchedPlace;
        setAllPlacesMap(prev => new Map(prev).set(placeId, fetchedPlace));
      } catch (error) {
        console.error('Erro ao buscar informações do local:', error);
        notify('Erro ao adicionar local', { type: 'error' });
        return;
      }
    }

    const product = formData.products[productIndex];

    // Buscar ProductPlace se for produto existente
    let partnerValue = null;
    let hasProductPlace = false;
    let productRtiCode: string | null = null;

    if (product.type === GiftProductType.Existing && product.productId) {
      try {
        const { data: catalogProduct } = await dataProvider.getOne(
          `chains/${chainId}/products`,
          { id: product.productId }
        );
        const productPlaces = catalogProduct?.places || catalogProduct?.productPlaces || [];
        const productPlace = productPlaces.find((pp: any) => pp.placeId === placeId);

        if (productPlace?.partnerPrice != null) {
          partnerValue = productPlace.partnerPrice;
          hasProductPlace = true;
        }
        // rtiCode automático do catálogo — independe do partnerValue (funciona mesmo com 0/null)
        if (productPlace?.rtiCode != null) {
          productRtiCode = String(productPlace.rtiCode);
        }
      } catch (error) {
        console.error('Erro ao buscar ProductPlace:', error);
      }
    }

    // Configuração de integração
    let rtiCode = null;
    let isEvoActive = false;
    let trevoServiceId: number | null = null;

    if (product.integrationType === IntegrationType.rti) {
      rtiCode = null;
      isEvoActive = false;
    } else if (product.integrationType === IntegrationType.evo) {
      rtiCode = null;
      isEvoActive = true;
    }

    const newPlaceConfig: PlaceConfig = {
      placeId: place?.id || placeId,
      placeName: place?.name || 'Local',
      integration: place?.integration,
      partnerValue,
      hasProductPlace,
      rtiCode,
      productRtiCode,
      isEvoActive,
      trevoServiceId,
      discountPercentage: null,
    };

    // Atualizar estado
    const updated = [...productsWithPlaces];
    const productConfig = findByProductIndex(updated, productIndex);
    if (!productConfig) { return; }
    productConfig.places.push(newPlaceConfig);
    setProductsWithPlaces(updated);
    updateGiftProductPlaces(updated);
  };

  const handleDeletePlace = (productIndex: number, placeId: string) => {
    console.log("🚀 ~ handleDeletePlace ~ productIndex:", productIndex, "placeId:", placeId);

    const updated = [...productsWithPlaces];
    const productConfig = findByProductIndex(updated, productIndex);
    if (!productConfig) { return; }
    productConfig.places = productConfig.places.filter((p) => p.placeId !== placeId);
    setProductsWithPlaces(updated);
    updateGiftProductPlaces(updated);
  };

  const handleUpdatePartnerValue = (productIndex: number, placeId: string, value: number) => {
    const updated = [...productsWithPlaces];
    const placeConfig = findByProductIndex(updated, productIndex)?.places.find((p) => p.placeId === placeId);
    if (placeConfig) {
      placeConfig.partnerValue = value;
      setProductsWithPlaces(updated);
      updateGiftProductPlaces(updated);
    }
  };

  const handleUpdateRtiCode = (productIndex: number, placeId: string, value: string) => {
    const updated = [...productsWithPlaces];
    const placeConfig = findByProductIndex(updated, productIndex)?.places.find((p) => p.placeId === placeId);
    if (placeConfig) {
      placeConfig.rtiCode = value;
      setProductsWithPlaces(updated);
      updateGiftProductPlaces(updated);
    }
  };

  const handleUpdateEvoActive = (productIndex: number, placeId: string, value: boolean) => {
    const updated = [...productsWithPlaces];
    const placeConfig = findByProductIndex(updated, productIndex)?.places.find((p) => p.placeId === placeId);
    if (placeConfig) {
      placeConfig.isEvoActive = value;
      setProductsWithPlaces(updated);
      updateGiftProductPlaces(updated);
    }
  };

  const handleUpdateTrevoServiceId = (productIndex: number, placeId: string, value: number | null) => {
    const updated = [...productsWithPlaces];
    const placeConfig = findByProductIndex(updated, productIndex)?.places.find((p) => p.placeId === placeId);
    if (placeConfig) {
      placeConfig.trevoServiceId = value;
      setProductsWithPlaces(updated);
      updateGiftProductPlaces(updated);
    }
  };

  const handleUpdateDiscountPercentage = (productIndex: number, placeId: string, value: number) => {
    const updated = [...productsWithPlaces];
    const placeConfig = findByProductIndex(updated, productIndex)?.places.find((p) => p.placeId === placeId);
    if (placeConfig) {
      placeConfig.discountPercentage = value;
      setProductsWithPlaces(updated);
      updateGiftProductPlaces(updated);
    }
  };

  const handleAddAllPlaces = async (productIndex: number) => {
    console.log("🚀 ~ handleAddAllPlaces ~ productIndex:", productIndex);

    const product = formData.products[productIndex];
    const currentConfig = findByProductIndex(productsWithPlaces, productIndex);
    if (!currentConfig) return;

    const currentPlaceIds = currentConfig.places.map(p => p.placeId);

    const placesToAdd = allAvailablePlaces.filter((place: any) => {
      // Já foi adicionado a este produto
      if (currentPlaceIds.includes(place.id)) return false;
      return true;
    });

    if (placesToAdd.length === 0) {
      notify('Todos os locais já foram adicionados a este produto', { type: 'info' });
      return;
    }

    setLoading(true);

    try {
      // Buscar ProductPlaces se for produto existente
      let productPlacesMap = new Map<string, any>();
      if (product.type === GiftProductType.Existing && product.productId) {
        try {
          const { data: catalogProduct } = await dataProvider.getOne(
            `chains/${chainId}/products`,
            { id: product.productId }
          );
          const productPlaces = catalogProduct?.places || catalogProduct?.productPlaces || [];
          productPlaces.forEach((pp: any) => {
            productPlacesMap.set(pp.placeId, pp);
          });
        } catch (error) {
          console.error('Erro ao buscar ProductPlace:', error);
        }
      }

      const newPlacesConfigs: PlaceConfig[] = placesToAdd.map((place) => {
        let partnerValue = null;
        let hasProductPlace = false;
        let productRtiCode: string | null = null;

        // Verificar se tem ProductPlace (valor automático)
        if (productPlacesMap.has(place.id)) {
          const productPlace = productPlacesMap.get(place.id);
          if (productPlace?.partnerPrice != null) {
            partnerValue = productPlace.partnerPrice;
            hasProductPlace = true;
          }
          // rtiCode automático do catálogo — independe do partnerValue (funciona mesmo com 0/null)
          if (productPlace?.rtiCode != null) {
            productRtiCode = String(productPlace.rtiCode);
          }
        }

        // Configuração de integração
        let rtiCode = null;
        let isEvoActive = false;
        let trevoServiceId: number | null = null;

        if (product.integrationType === IntegrationType.rti) {
          rtiCode = null;
          isEvoActive = false;
        } else if (product.integrationType === IntegrationType.evo) {
          rtiCode = null;
          isEvoActive = true;
        }

        return {
          placeId: place.id,
          placeName: place.name,
          integration: place.integration,
          partnerValue,
          hasProductPlace,
          rtiCode,
          productRtiCode,
          isEvoActive,
          trevoServiceId,
          discountPercentage: null,
        };
      });

      // Atualizar o allPlacesMap com os novos lugares
      const updatedMap = new Map(allPlacesMap);
      placesToAdd.forEach(place => {
        updatedMap.set(place.id, place);
      });
      setAllPlacesMap(updatedMap);

      // Atualizar estado
      const updated = [...productsWithPlaces];
      const productConfig = findByProductIndex(updated, productIndex);
      if (!productConfig) return;
      productConfig.places.push(...newPlacesConfigs);
      setProductsWithPlaces(updated);
      updateGiftProductPlaces(updated);

      notify(`${placesToAdd.length} ${placesToAdd.length === 1 ? 'local adicionado' : 'locais adicionados'} com sucesso`, { type: 'success' });
    } catch (error) {
      console.error('Erro ao adicionar todos os locais:', error);
      notify('Erro ao adicionar locais', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const showChainMissing = !formData.chainId;
  const showNoProducts = !formData.products?.length;

  // Verificar se TODOS os produtos são FuelDiscount (não precisam configurar locais)
  const allProductsAreFuelDiscount = formData.products?.length > 0 &&
    formData.products.every((p: GiftProduct) => p.type === GiftProductType.FuelDiscount);

  // Recompensas que não geram cupom não têm local de troca: Direito ao Palpite
  // (vira palpite no bolão), Número da Sorte (vira número no sorteio) e
  // Giro na Roleta (vira giro; o prêmio só nasce quando a roda para).
  const allProductsWithoutPlaces = formData.products?.length > 0 &&
    formData.products.every((p: GiftProduct) => p.type === GiftProductType.PredictionTicket
      || p.type === GiftProductType.DrawingNumber
      || p.type === GiftProductType.SpinTicket);
  const hasDrawingNumber = (formData.products || []).some(
    (p: GiftProduct) => p.type === GiftProductType.DrawingNumber,
  );

  // Recompensa em dinheiro não tem local de troca a escolher: o cupom vale em qualquer
  // local da rede que aceite cashback.
  const cashbackProductsCount = (formData.products || []).filter(
    (p: GiftProduct) => (p.cashValue ?? null) !== null,
  ).length;
  // Vale também quando o cashback divide o brinde com outra recompensa sem local: aí NADA
  // aparece na lista, e sem esta mensagem a tela diria "use o botão Adicionar" sem ter o que.
  const showCashbackOnlyNotice = cashbackProductsCount > 0
    && everyProductSkipsPlaces(formData.products || []);
  const hasCashbackWithOthers = cashbackProductsCount > 0 && !showCashbackOnlyNotice;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 8 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Locais de Resgate por Produto
        </Typography>

        <Typography variant="body2" color="text.secondary" mb={3}>
          Configure os locais onde cada produto pode ser resgatado e o valor que cada parceiro receberá.
        </Typography>

        {showChainMissing ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
            <Typography>* Selecione uma rede na barra de ferramentas acima</Typography>
          </Paper>
        ) : showNoProducts ? (
          <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
            <Typography color="text.secondary" gutterBottom>
              Você precisa adicionar produtos antes de configurar os locais de resgate.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Volte ao passo "Produto" para adicionar produtos ao brinde.
            </Typography>
          </Paper>
        ) : allProductsAreFuelDiscount ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'success.light',
              borderRadius: 2,
              mb: 2,
              bgcolor: '#f0fdf4',
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22, mt: 0.2 }} />
              <Box>
                <Typography variant="body1" fontWeight={600} color="success.dark" mb={0.5}>
                  Configuração automática
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Os locais de resgate são herdados do desconto vinculado. Prossiga para o próximo passo.
                </Typography>
              </Box>
            </Box>
          </Paper>
        ) : showCashbackOnlyNotice ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'success.light',
              borderRadius: 2,
              mb: 2,
              bgcolor: '#f0fdf4',
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 22, mt: 0.2 }} />
              <Box>
                <Typography variant="body1" fontWeight={600} color="success.dark" mb={0.5}>
                  Sem locais de troca
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A recompensa em dinheiro vale em qualquer local da rede que aceite cashback. Prossiga para o próximo passo.
                </Typography>
              </Box>
            </Box>
          </Paper>
        ) : allProductsWithoutPlaces ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #7869DF',
              borderRadius: 2,
              mb: 2,
              bgcolor: '#ede9fe',
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <CheckCircleIcon sx={{ color: '#7869DF', fontSize: 22, mt: 0.2 }} />
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ color: '#7869DF' }} mb={0.5}>
                  Sem locais de troca
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {hasDrawingNumber
                    ? 'Recompensa Número da Sorte não gera cupom resgatável em posto — o cliente recebe os números do sorteio vinculado. Prossiga para o próximo passo.'
                    : 'Recompensa Direito ao Palpite não gera cupom resgatável em posto — o cliente recebe um ticket de palpite no bolão vinculado. Prossiga para o próximo passo.'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ) : productsWithPlaces.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              mb: 2,
            }}
          >
            {placesLoading || loading ? (
              <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                <CircularProgress size={20} />
                <Box>
                  <Typography variant="subtitle2">Carregando configurações...</Typography>
                  <Typography variant="body2" color="text.secondary">Aguarde enquanto carregamos os locais e configurações dos produtos.</Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Typography color="text.secondary" gutterBottom>
                  Nenhum local configurado para os produtos.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use o botão "Adicionar" em cada produto para começar a configurar locais.
                </Typography>
              </>
            )}
          </Paper>
        ) : (
          <Box>
            {hasCashbackWithOthers && (
              <Alert severity="info" sx={{ mb: 2 }}>
                A recompensa em dinheiro não aparece aqui: ela vale em qualquer local da rede que aceite cashback.
              </Alert>
            )}
            {productsWithPlaces.map((productWithPlaces) => {
              // Obter apenas os locais já selecionados NESTE produto específico
              const selectedPlacesForThisProduct = productWithPlaces.places.map((pl) => pl.placeId);

              return (
                <ProductConfigCard
                  key={productWithPlaces.productIndex}
                  product={productWithPlaces.product}
                  productIndex={productWithPlaces.productIndex}
                  placesConfigs={productWithPlaces.places}
                  onAddPlace={(placeId) => handleAddPlace(productWithPlaces.productIndex, placeId)}
                  onAddAllPlaces={() => handleAddAllPlaces(productWithPlaces.productIndex)}
                  onDeletePlace={(placeId) => handleDeletePlace(productWithPlaces.productIndex, placeId)}
                  onUpdatePartnerValue={(placeId, value) =>
                    handleUpdatePartnerValue(productWithPlaces.productIndex, placeId, value)
                  }
                  onUpdateRtiCode={(placeId, value) =>
                    handleUpdateRtiCode(productWithPlaces.productIndex, placeId, value)
                  }
                  onUpdateEvoActive={(placeId, value) =>
                    handleUpdateEvoActive(productWithPlaces.productIndex, placeId, value)
                  }
                  onUpdateTrevoServiceId={(placeId, value) =>
                    handleUpdateTrevoServiceId(productWithPlaces.productIndex, placeId, value)
                  }
                  onUpdateDiscountPercentage={(placeId, value) =>
                    handleUpdateDiscountPercentage(productWithPlaces.productIndex, placeId, value)
                  }
                  fuels={fuels}
                  isLoading={loading}
                  alreadySelectedPlaces={selectedPlacesForThisProduct}
                  availablePlacesCount={allAvailablePlaces.length}
                />
              );
            })}
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

// Wrapper com FormDataConsumer para passar formData
const FormPlacesConfigurationWrapper: React.FC<{ fuels?: any[]; onLoadingChange?: (loading: boolean) => void }> = ({ fuels, onLoadingChange }) => {
  return (
    <FormDataConsumer>
      {({ formData }) => <FormPlacesConfiguration formData={formData} fuels={fuels} onLoadingChange={onLoadingChange} />}
    </FormDataConsumer>
  );
};

export default FormPlacesConfigurationWrapper;
